import FlexBox from '@/atomic-components/FlexBox';
import { Box, Button } from '@mui/material';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import TruckIcon2 from '@/assets/TruckIcon2.svg';
import { getColor } from '@/utils/theme/colors';
import { useLocation } from '@/hooks/useLocation';
import { getStyle } from '@/utils/theme/fonts';
import { useEffect, useState } from 'react';
import { locationService } from '@/services/location';
import { TAvailableVertical } from '@/services/location/models/check-pincode-serviceability';
import { ApiState } from '@/models/generics';
import { emitMoeEvent } from '@/utils/dataLayer';
import { useCart } from '@/hooks/useCart';
import { useUser } from '@/hooks/useUser';

import { useRouter } from 'next/router';
import ArrowWithoutTail from '@/assets/ArrowWithoutTail';
type DeliveryEstimateCardProps = {
	pincode?: number;
	verticals: string[];
	bordered?: boolean;
	price?: number;
	hidePincode?: boolean;
	showText?: boolean;
	text?: string;
	deliveryAddress?: any;
	isDeliveryAddressAvailable?: any;
};

const DeliveryEstimateCard: React.FC<DeliveryEstimateCardProps> = (props) => {
	const { selectedLocation, locationActions } = useLocation();
	const [estimateDeliveryDate, setEstimateDeliveryDate] = useState<string>('');
	const [checkPincodeViewState, setCheckPincodeViewState] = useState<ApiState>(
		ApiState.IDLE
	);
	const { addresses } = useUser();

	const router = useRouter();
	const { getCartAttributes, rentalCartItemsCount, buyCartItemsCount } =
		useCart();

	const { hidePincode = false, showText = false, text } = props;
	const pincode = props.pincode ?? selectedLocation.pincode;

	function openLocationSelector() {
		locationActions.triggerCitySelector(true);
	}
	function getPincodeDetails() {
		setCheckPincodeViewState(ApiState.LOADING);
		locationService
			.getPincodeServiceabilityService(pincode as number)
			.then((res) => {
				if (res.data?.success) {
					setEstimateDeliveryDate(
						getEstimateDeliveryDate(res.data?.data.availableVerticals)
					);
				}
			})
			.finally(() => setCheckPincodeViewState(ApiState.IDLE));
	}

	function getEstimateDeliveryDate(
		availableVerticals: TAvailableVertical[]
	): string {
		let maxPromisedDate = 2;
		let dateToShow = '';
		const verticalsToCheck = availableVerticals?.filter((vertical) =>
			props.verticals?.includes(vertical.vertical)
		);

		verticalsToCheck?.map((vertical) => {
			const deliveryService = vertical.services.find(
				(service) => service.type === 'DELIVERY'
			);

			if (deliveryService?.promisedCustomerLeadTimeInDays > maxPromisedDate) {
				maxPromisedDate = deliveryService?.promisedCustomerLeadTimeInDays;
				dateToShow = deliveryService?.displayValue as string;
			}
			dateToShow = deliveryService?.displayValue as string;
		});

		return dateToShow;
	}

	useEffect(() => {
		getPincodeDetails();
	}, [pincode]);

	const attributes = getCartAttributes();

	function changeAddress() {
		router.push({
			pathname: '/checkout/address',
			query: router.query,
		});
		emitMoeEvent('Change Address Clicked', {
			cart_type:
				rentalCartItemsCount > 0 ? 'RENT' : buyCartItemsCount > 0 ? 'BUY' : '',
			vertical: attributes?.verticalsString,
			default_address: addresses?.defaultAddress?.id ? true : false,
		});
	}

	return (
		<FlexBox
			p='12px 24px'
			sx={{
				borderRadius: '13px',
				border: `1px solid ${getColor('Gray -4')}`,
			}}
			alignItems='center'
			justifyContent='flex-start'
		>
			{!props?.isDeliveryAddressAvailable ? (
				<FlexBox alignItems='center' justifyContent='flex-start' width='100%'>
					<Box sx={{ mr: '16px' }}>
						<TruckIcon2 />
					</Box>
					<Box>
						<LencoTypography color={getColor('Gray 0')}>
							Delivery Estimate
						</LencoTypography>

						{showText ? (
							<LencoTypography
								sx={{ ...getStyle('Small_Regular') }}
								color={getColor('Gray -3')}
							>
								{text}
							</LencoTypography>
						) : (
							<FlexBox alignItems='center' mt='4px'>
								<LencoTypography
									variant='Small_Medium'
									color={getColor('Gray 0')}
								>
									{estimateDeliveryDate}{' '}
								</LencoTypography>

								{!hidePincode && pincode && (
									<>
										<LencoTypography
											variant='Small_Regular'
											mx='4px'
											color={getColor('Gray -3')}
										>
											to
										</LencoTypography>
										<LencoTypography
											variant='Small_Medium'
											color={getColor('Minty 0')}
											sx={{ textDecoration: 'underline' }}
											className='cursor-pointer'
											onClick={openLocationSelector}
										>
											{pincode}
										</LencoTypography>
									</>
								)}
							</FlexBox>
						)}
					</Box>

					<FlexBox
						alignItems='center'
						ml='auto'
						direction='column'
						justifyContent='center'
					>
						<LencoTypography
							variant='Tiny_Medium'
							color={getColor('Gray -4')}
							sx={{ textDecorationLine: 'line-through', ml: '4px' }}
						>
							₹499
						</LencoTypography>
						<LencoTypography
							variant='Tiny_Medium'
							color={getColor('Success 0')}
						>
							FREE
						</LencoTypography>
					</FlexBox>
				</FlexBox>
			) : (
				<FlexBox
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
					}}
				>
					<FlexBox direction='column' gap='4px' justifyContent='space-between'>
						<LencoTypography
							sx={{ ...getStyle('Small_Medium') }}
							color={getColor('Gray 0')}
						>
							{estimateDeliveryDate} to:{' '}
							<span style={{ ...getStyle('Small_SemiBold') }}>
								{props.deliveryAddress?.name}
							</span>
						</LencoTypography>
						<LencoTypography
							sx={{
								...getStyle('Small_SemiBold'),
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								maxWidth: '600px',
								display: 'block',
							}}
							color={getColor('Gray -3')}
						>
							{props.deliveryAddress?.address}
						</LencoTypography>
					</FlexBox>
					<FlexBox alignItems='center' gap='8px'>
						<Button
							sx={{
								border: `1px solid ${getColor('Minty 0')}`,
								...getStyle('Tiny_Medium'),
								color: getColor('Minty 0'),
								borderRadius: '24px',
								p: '3px 12px',
							}}
							onClick={changeAddress}
						>
							Change{' '}
							<span style={{ marginLeft: '8px', transform: 'rotate(-90deg)' }}>
								<ArrowWithoutTail
									color={getColor('Minty -1')}
									height={8}
									width={10}
								/>
							</span>
						</Button>
					</FlexBox>
				</FlexBox>
			)}
		</FlexBox>
	);
};

export default DeliveryEstimateCard;
